import React, { useState, useEffect } from 'react';
import { Copy, Check, Calculator } from 'lucide-react';

type CalcMode = 'whatPercent' | 'percentOf' | 'percentChange' | 'addPercent' | 'subtractPercent';

const PercentageCalculator: React.FC = () => {
  const [mode, setMode] = useState<CalcMode>('percentOf');
  const [value1, setValue1] = useState('25');
  const [value2, setValue2] = useState('200');
  const [result, setResult] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    calculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, value1, value2]);

  const calculate = () => {
    const v1 = parseFloat(value1);
    const v2 = parseFloat(value2);
    if (isNaN(v1) || isNaN(v2)) { setResult(''); return; }

    let res: number;
    switch (mode) {
      case 'whatPercent': res = (v1 / v2) * 100; break;
      case 'percentOf': res = (v1 / 100) * v2; break;
      case 'percentChange': res = ((v2 - v1) / v1) * 100; break;
      case 'addPercent': res = v2 + (v2 * v1 / 100); break;
      case 'subtractPercent': res = v2 - (v2 * v1 / 100); break;
      default: res = 0;
    }
    setResult(res.toFixed(4).replace(/\.?0+$/, ''));
  };

  const copyResult = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const modes = [
    { id: 'percentOf' as CalcMode, label: 'X% of Y', desc: 'What is X% of Y?' },
    { id: 'whatPercent' as CalcMode, label: 'X is what % of Y', desc: 'X is what percent of Y?' },
    { id: 'percentChange' as CalcMode, label: '% Change', desc: 'Percent change from X to Y' },
    { id: 'addPercent' as CalcMode, label: 'Add %', desc: 'Add X% to Y' },
    { id: 'subtractPercent' as CalcMode, label: 'Subtract %', desc: 'Subtract X% from Y' },
  ];

  const getLabels = () => {
    switch (mode) {
      case 'whatPercent': return { l1: 'Value', l2: 'Total', unit: '%' };
      case 'percentOf': return { l1: 'Percentage (%)', l2: 'Value', unit: '' };
      case 'percentChange': return { l1: 'From', l2: 'To', unit: '%' };
      case 'addPercent': return { l1: 'Percentage (%)', l2: 'Value', unit: '' };
      case 'subtractPercent': return { l1: 'Percentage (%)', l2: 'Value', unit: '' };
      default: return { l1: 'Value 1', l2: 'Value 2', unit: '' };
    }
  };

  const labels = getLabels();

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Percentage Calculator</h2>
        <p className="text-gray-600">Calculate percentages quickly and easily</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        <div className="bg-gray-50 rounded-xl p-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">Calculation Type</label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {modes.map((m) => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  mode === m.id
                    ? 'bg-amber-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-2 text-center">
            {modes.find(m => m.id === mode)?.desc}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{labels.l1}</label>
              <input
                type="number"
                value={value1}
                onChange={(e) => setValue1(e.target.value)}
                className="w-full px-4 py-3 text-xl font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{labels.l2}</label>
              <input
                type="number"
                value={value2}
                onChange={(e) => setValue2(e.target.value)}
                className="w-full px-4 py-3 text-xl font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-amber-50 rounded-xl border border-amber-200 p-6 text-center">
          <p className="text-sm text-gray-600 mb-2">Result</p>
          <div className="flex items-center justify-center gap-2">
            <p className="text-4xl font-bold text-amber-600">
              {result}{labels.unit}
            </p>
            <button onClick={copyResult} className="p-2 text-gray-400 hover:text-gray-600">
              {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <div className="flex items-start gap-3">
            <Calculator className="w-5 h-5 text-gray-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900 mb-1">Quick Formulas</h4>
              <p className="text-sm text-gray-600">
                X% of Y = (X/100) × Y • X is what % of Y = (X/Y) × 100 • % Change = ((New-Old)/Old) × 100
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PercentageCalculator;
